/**
 * Brings a person back to the consent page (`/connect`) after a sign-in that lost
 * its address.
 *
 * A signed-out visitor sees the login page on the address they opened, so a
 * password or passkey sign-in on `/connect?...` simply renders the consent page
 * next. Google sign-in does not: the API sends every Google landing to the account
 * root. So while the person is signed out on `/connect`, the address waits here
 * (sessionStorage, this tab only), and the first signed-in screen takes it back
 * once, if it is younger than 15 minutes.
 */

const KEY = "wa_connect_return";
export const CONNECT_RETURN_MAX_AGE_MS = 15 * 60_000;

/** Only the consent page itself is ever stored or followed. */
export function isConnectPath(path: string): boolean {
  return path === "/connect" || path.startsWith("/connect?");
}

/** The stored value to the address to go to, or null when it is missing, broken,
 *  not the consent page, or too old. */
export function parseConnectReturn(raw: string | null, now: number): string | null {
  if (!raw) return null;
  try {
    const saved = JSON.parse(raw) as { path?: unknown; at?: unknown };
    if (typeof saved.path !== "string" || typeof saved.at !== "number") return null;
    if (!isConnectPath(saved.path)) return null;
    const age = now - saved.at;
    if (age < 0 || age > CONNECT_RETURN_MAX_AGE_MS) return null;
    return saved.path;
  } catch {
    return null;
  }
}

export function saveConnectReturn(path: string, now: number = Date.now()): void {
  if (!isConnectPath(path)) return;
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify({ path, at: now }));
  } catch {
    // Storage blocked (private mode, strict settings): Google sign-in then lands
    // on the account root, as it did before this helper existed.
  }
}

/** Reads and removes the saved address in one step, so it is followed once. */
export function takeConnectReturn(now: number = Date.now()): string | null {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    window.sessionStorage.removeItem(KEY);
    return parseConnectReturn(raw, now);
  } catch {
    return null;
  }
}
